/**
 * Floating upload indicator. Shows a quiet glass pill in the bottom-right
 * whenever any upload is in flight. Click to expand into a per-file panel.
 *
 * Aesthetic intent: Apple-style. Restrained, glassy, subtle motion. Never
 * blocks the underlying UI; only present when there's something happening.
 *
 * Wired to the global useUploadStore — every uploadAsset() call automatically
 * registers and animates here without any per-call boilerplate.
 */
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, X, Check, AlertCircle, Image as ImageIcon, Film, Upload } from 'lucide-react';
import { useUploadStore, type UploadEntry, type UploadKind } from '@/store/uploadStore';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

const KIND_ICON: Record<UploadKind, React.ElementType> = {
  image: ImageIcon,
  video: Film,
  thumbnail: ImageIcon,
  other: Upload,
};

function fmtBytes(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function aggregateProgress(uploads: UploadEntry[]): number {
  const inFlight = uploads.filter((u) => u.status !== 'done' && u.status !== 'error');
  if (inFlight.length === 0) return 1;
  const totalBytes = inFlight.reduce((s, u) => s + u.totalBytes, 0);
  if (totalBytes === 0) return 0;
  const weighted = inFlight.reduce((s, u) => s + u.totalBytes * u.progress, 0);
  return weighted / totalBytes;
}

function UploadRow({ entry }: { entry: UploadEntry }) {
  const { t } = useTranslation();
  const dismiss = useUploadStore((s) => s.dismiss);
  const Icon = KIND_ICON[entry.kind] ?? Upload;
  const pct = Math.round(entry.progress * 100);
  const isDone = entry.status === 'done';
  const isError = entry.status === 'error';
  const isFinalizing = entry.status === 'finalizing';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[var(--color-surface-hover)] transition-colors group"
    >
      {entry.thumbUrl ? (
        // Real preview of the asset being uploaded; status shown as a corner badge.
        <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-[var(--color-surface-hover)]">
          <img src={entry.thumbUrl} alt="" className="h-full w-full object-cover" />
          {(isError || isDone) && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center backdrop-blur-[1px]',
                isError ? 'bg-[var(--color-destructive)]/45 text-white' : 'bg-[var(--color-accent)]/45 text-white',
              )}
            >
              {isError ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" strokeWidth={2.5} />}
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'h-8 w-8 rounded-lg flex items-center justify-center shrink-0',
            isError
              ? 'bg-[var(--color-destructive)]/15 text-[var(--color-destructive)]'
              : isDone
                ? 'bg-[var(--color-accent)]/15 text-[var(--color-accent)]'
                : 'bg-[var(--color-primary)]/12 text-[var(--color-primary)]',
          )}
        >
          {isError ? (
            <AlertCircle className="w-4 h-4" />
          ) : isDone ? (
            <Check className="w-4 h-4" strokeWidth={2.5} />
          ) : (
            <Icon className="w-4 h-4" />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-medium text-[var(--color-text)] truncate">
            {entry.label}
          </div>
          <div
            className={cn(
              'text-[11px] tabular-nums shrink-0',
              isError
                ? 'text-[var(--color-destructive)]'
                : isDone
                  ? 'text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)]',
            )}
          >
            {isError
              ? t('componentsMisc:uploadIndicator.error')
              : isDone
                ? t('componentsMisc:uploadIndicator.done')
                : isFinalizing
                  ? t('componentsMisc:uploadIndicator.finalizing')
                  : `${pct}%`}
          </div>
        </div>
        {!isError && !isDone && (
          <div className="mt-1.5 h-1 rounded-full bg-[var(--color-border)] overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)]"
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            />
          </div>
        )}
        {isError && entry.errorMessage && (
          <div className="text-[11px] text-[var(--color-destructive)]/80 truncate mt-0.5">
            {entry.errorMessage}
          </div>
        )}
        <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
          {fmtBytes(entry.totalBytes)}
        </div>
      </div>
      {(isDone || isError) && (
        <button
          onClick={() => dismiss(entry.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-[var(--color-surface-elevated)] text-[var(--color-text-muted)]"
          aria-label={t('componentsMisc:uploadIndicator.dismiss')}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </motion.div>
  );
}

export function UploadIndicator() {
  const { t } = useTranslation();
  const uploads = useUploadStore((s) => s.uploads);
  const [expanded, setExpanded] = useState(false);

  const list = useMemo(
    () => Object.values(uploads).sort((a, b) => b.startedAt - a.startedAt),
    [uploads],
  );
  const activeCount = list.filter(
    (u) => u.status === 'uploading' || u.status === 'finalizing',
  ).length;
  const errorCount = list.filter((u) => u.status === 'error').length;
  const aggProgress = aggregateProgress(list);
  const aggPct = Math.round(aggProgress * 100);

  // Browser-level "are you sure?" prompt while uploads are in flight.
  // Native dialog only — no custom UI here, the floating widget is enough.
  useEffect(() => {
    if (activeCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [activeCount]);

  // Auto-collapse when everything is done
  useEffect(() => {
    if (list.length === 0 && expanded) setExpanded(false);
  }, [list.length, expanded]);

  if (list.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[60] pointer-events-none">
      <AnimatePresence mode="wait">
        {!expanded ? (
          <motion.button
            key="collapsed"
            layout
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            onClick={() => setExpanded(true)}
            className={cn(
              'pointer-events-auto inline-flex items-center gap-3 pl-3 pr-3.5 py-2.5 rounded-full',
              'bg-[var(--color-surface)]/85 backdrop-blur-2xl backdrop-saturate-150',
              'border border-[var(--color-border)] shadow-[0_8px_30px_-8px_rgba(0,0,0,0.35)]',
              'hover:scale-[1.015] active:scale-[0.99] transition-transform duration-150',
            )}
          >
            <div className="relative h-7 w-7 shrink-0">
              {/* Ring progress */}
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 28 28">
                <circle
                  cx="14"
                  cy="14"
                  r="11"
                  fill="none"
                  className="stroke-[var(--color-border)]"
                  strokeWidth="2.5"
                />
                <motion.circle
                  cx="14"
                  cy="14"
                  r="11"
                  fill="none"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className={
                    errorCount > 0
                      ? 'stroke-[var(--color-destructive)]'
                      : 'stroke-[var(--color-primary)]'
                  }
                  initial={false}
                  animate={{
                    strokeDasharray: `${aggProgress * 69.115} 69.115`,
                  }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                {activeCount > 0 ? (
                  <motion.div
                    className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]"
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                ) : errorCount > 0 ? (
                  <AlertCircle className="w-3.5 h-3.5 text-[var(--color-destructive)]" />
                ) : (
                  <Check className="w-3.5 h-3.5 text-[var(--color-accent)]" strokeWidth={2.5} />
                )}
              </div>
            </div>
            <div className="text-left">
              <div className="text-[13px] font-medium text-[var(--color-text)] leading-tight">
                {activeCount > 0
                  ? t('componentsMisc:uploadIndicator.uploading', { count: activeCount })
                  : errorCount > 0
                    ? t('componentsMisc:uploadIndicator.failures', { count: errorCount })
                    : t('componentsMisc:uploadIndicator.allDone')}
              </div>
              {activeCount > 0 && (
                <div className="text-[10.5px] tabular-nums text-[var(--color-text-muted)] leading-tight">
                  {aggPct}% · {t('componentsMisc:uploadIndicator.dontCloseTab')}
                </div>
              )}
            </div>
            <ChevronUp className="w-3.5 h-3.5 text-[var(--color-text-muted)] ml-0.5" />
          </motion.button>
        ) : (
          <motion.div
            key="expanded"
            layout
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className={cn(
              'pointer-events-auto w-[340px] max-w-[calc(100vw-2.5rem)] rounded-2xl overflow-hidden',
              'bg-[var(--color-surface)]/90 backdrop-blur-2xl backdrop-saturate-150',
              'border border-[var(--color-border)] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.45)]',
            )}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
              <div>
                <div className="text-[13px] font-semibold text-[var(--color-text)]">
                  {activeCount > 0
                    ? t('componentsMisc:uploadIndicator.sending', { count: activeCount })
                    : t('componentsMisc:uploadIndicator.recentUploads')}
                </div>
                {activeCount > 0 && (
                  <div className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
                    {t('componentsMisc:uploadIndicator.dontCloseUntilDone')}
                  </div>
                )}
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="p-1.5 rounded-md hover:bg-[var(--color-surface-elevated)] text-[var(--color-text-muted)] transition-colors"
                aria-label={t('componentsMisc:uploadIndicator.collapse')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[60dvh] overflow-y-auto p-1.5">
              <AnimatePresence initial={false}>
                {list.map((u) => (
                  <UploadRow key={u.id} entry={u} />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
